import { animate, style, transition, trigger } from '@angular/animations';
import { AfterViewChecked, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownAreas, KnownInstrumentationEvents, KnownReferralPlatform } from '../../shared/instrumentation/instrumentation.service';
import { FileItem, FileType } from '../../shared/models/file-item.model';
import { ObservableScope } from '../../shared/models/observable-scope.enum';
import { EventService } from '../../shared/services/event.service';
import { AngularEvents } from '../../shared/shared-context/angular-events';
import { CapabilitiesService } from '../../shared/shared-context/capabilities.service';
import { ContextService } from '../../shared/shared-context/context.service';
import { AppSpecificResource, LocService } from '../../shared/shared-context/loc.service';
import { FeaturedDocument, FeaturedService } from './featured.service';

import './featured.component.css';

@Component({
    selector: 'ohp-featured-component',
    templateUrl: './featured.component.html',
    animations: [
        trigger('fadeIn', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('500ms 0ms linear', style({ opacity: 1 }))
            ])
        ])
    ]
})
export class FeaturedComponent implements AfterViewChecked, OnDestroy, OnInit {

    @Input() public app: FileType;

    @Input() public visible: boolean;

    @ViewChild('featuredDocsElement') public featuredDocsElement: ElementRef;

    public appName: string;

    public featuredDocs: Array<FeaturedDocument>;
    public isLoading: boolean;
    public isError: boolean;
    public isCollapsedView: boolean;

    public title: string;
    public showMoreText: string;
    public showMoreAriaLabel: string;
    public showLessText: string;
    public showLessAriaLabel: string;
    public errorMessage: string;
    public stateAnnouncement: string;
    public fluentRefresh: boolean;

    public defaultLinkTarget: string;

    public maxItemsPerLine: number;

    private readonly eventStream: Subscription;
    private readonly area = KnownAreas.FeaturedDocs;
    private tileWidth: number;

    constructor(
        private readonly instrumentationService: InstrumentationService,
        private readonly capabilitiesService: CapabilitiesService,
        contextService: ContextService,
        private readonly locService: LocService,
        private readonly featuredService: FeaturedService,
        private readonly eventService: EventService
    ) {
        this.defaultLinkTarget = contextService.sharedContext.defaultLinkTarget;
        this.eventStream = this.eventService.events.withScope(ObservableScope.OnlyFuture).pipe(
            filter(event => event.name === AngularEvents.contentWidthChanged || event.name === AngularEvents.windowResized))
            .subscribe(() => this.updateMaxItemsPerLine());

        this.isCollapsedView = true;
        this.maxItemsPerLine = 0;
    }

    public ngAfterViewChecked() {
        if (!this.maxItemsPerLine) {
            setTimeout(() => {
                this.updateMaxItemsPerLine();
            });
        }
    }

    public ngOnDestroy() {
        if (this.eventStream) {
            this.eventStream.unsubscribe();
        }
    }

    public ngOnInit() {
        this.appName = FileType[this.app].toLowerCase();
        this.fluentRefresh = this.capabilitiesService.capabilities.fluentRefresh;
        this.tileWidth = this.fluentRefresh ? 196 : 190;
        this.title = this.locService.appResource(AppSpecificResource.FeaturedDocumentsTitle, this.app);
        this.showMoreText = this.locService.appResource(AppSpecificResource.FeaturedDocumentsShowMore, this.app);
        this.showMoreAriaLabel = this.locService.appResource(AppSpecificResource.FeaturedDocumentsShowMoreAriaLabel, this.app);
        this.showLessText = this.locService.appResource(AppSpecificResource.FeaturedDocumentsShowLess, this.app);
        this.showLessAriaLabel = this.locService.appResource(AppSpecificResource.FeaturedDocumentsShowLessAriaLabel, this.app);
        this.errorMessage = this.locService.appResource(AppSpecificResource.FeaturedDocumentsError, this.app);

        this.isCollapsedView = true;
        this.isLoading = true;
        this.isError = false;

        this.featuredService.getFeaturedDocuments(this.app)
            .then(featuredDocs => {
                this.featuredDocs = featuredDocs;
            })
            .catch(() => {
                this.isError = true;
            })
            .then(() => {
                this.isLoading = false;
                this.instrumentFeaturedImpression();
            });
    }

    public toggle() {
        this.isCollapsedView = !this.isCollapsedView;
        this.stateAnnouncement = this.isCollapsedView ? this.showLessText : this.showMoreText;

        const params: InstrumentationParams = {};
        params[InstrumentationParam.Action] = this.isCollapsedView ? 'showLess' : 'showMore';
        this.instrumentationService.logClickEvent(KnownInstrumentationEvents.FeaturedDocumentsToggle, 'hide-show-featured-documents', this.area, null, null, params);
    }

    public onDocumentClick(featured: FeaturedDocument) {
        const referral = FileItem.referralFromFileType(this.app);
        this.instrumentationService.logClickEvent(KnownInstrumentationEvents.FeaturedDocumentsClick, featured.Id, this.area, referral, KnownReferralPlatform.Web);
    }

    public trackByIndex(index: number) {
        return index;
    }

    private updateMaxItemsPerLine() {
        if (this.featuredDocsElement && this.featuredDocsElement.nativeElement) {
            const width = this.featuredDocsElement.nativeElement.getBoundingClientRect().width;
            this.maxItemsPerLine = Math.floor(width / this.tileWidth);
        }
    }

    private instrumentFeaturedImpression() {
        const params: InstrumentationParams = {};
        let eventName: KnownInstrumentationEvents;

        if (this.isError) {
            eventName = KnownInstrumentationEvents.FeaturedDocumentsErrorImpression;
        } else {
            eventName = KnownInstrumentationEvents.FeaturedDocumentsImpression;
            params[InstrumentationParam.TotalSize] = this.featuredDocs ? this.featuredDocs.length.toString() : '';
        }

        this.instrumentationService.logImpressionEvent(eventName, params);
    }
}
